/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TableUtility } from '@utility/dynamic-table';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import { getStaffingRequestBaseURL } from '../../../../Api/request/staffingRequest';
import StaffingRequestToolbarHeading from './StaffingRequestToolbarHeading';
import { MODULE_NAMES } from '../../../../Constants/modules';
import { useLocation, useNavigate } from 'react-router-dom';

const normalizeRoleDetails = (roleDetails) => (
  Array.isArray(roleDetails)
    ? roleDetails
    : roleDetails
      ? [roleDetails]
      : []
);

const normalizeEmployeeId = (userProfile = {}) => (
  userProfile?.employeeId
  ?? userProfile?.employee_id
  ?? userProfile?.emp_id
  ?? userProfile?.empId
  ?? userProfile?.id
  ?? ''
);

const buildUserInfo = (userProfile = {}) => ({
  emailId: userProfile?.emailId ?? userProfile?.email ?? '',
  permissions: userProfile?.permissions || {},
  roleDetails: normalizeRoleDetails(userProfile?.roleDetails),
  employeeId: normalizeEmployeeId(userProfile),
  globalColors: userProfile?.siteConfiguration?.global_colors || userProfile?.globalColors || {},
});

const getProjectFilterState = () => {
  const activeItems = Array.from(document.querySelectorAll(
    '.tu-filter-column-item.active, .tu-filter-subitem.active, .tu-filter-detail-action.active',
  ));
  const hasActiveProjectName = activeItems.some((item) => (
    String(item?.textContent || '')
      .trim()
      .toLowerCase() === 'project name'
  ));

  if (!hasActiveProjectName) return null;

  const activePanel = Array.from(document.querySelectorAll('.tu-filter-template-panel'))
    .find((panel) => panel.querySelector('.tu-multi-select-filter'));
  if (!activePanel) return null;

  const selectedChips = activePanel.querySelectorAll(
    '.tu-multi-select-chip-row .custom-chip:not(.tu-multi-select-count-chip)',
  );
  return selectedChips.length > 0;
};

const StaffingRequestFilterUtility = ({
  userProfile,
  selectedCount,
  canShowEgm,
  egmDetails,
  onOpenUpdateStatus,
  onCheckEgm,
  onOpenNewRequest,
  canOpenNewRequest,
  hasApprovalAccess,
  filters,
  tableResetToken,
  onFilteredResponse,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const baseURL = useMemo(() => getStaffingRequestBaseURL(), []);
  const userInfo = useMemo(() => buildUserInfo(userProfile), [userProfile]);
  const isUserInfoReady = Boolean(userInfo.emailId || userInfo.employeeId || userInfo.id);
  const containerRef = useRef(null);
  const filterButtonSlotRef = useRef(null);
  const [pendingProjectFilterHasSelection, setPendingProjectFilterHasSelection] = useState(null);
  const permissions = userProfile?.permissions?.[MODULE_NAMES.DELIVERY_ADD_STAFFING_REQUEST] || [];

  const hasAccess = (action) => {
    return permissions.includes(action);
  };
  const handleFilteredResponse = useCallback((response) => (
    onFilteredResponse(response)
  ), [onFilteredResponse]);

  // This effect runs once on mount to clean the browser history state projectId while refreshing the page
  useEffect(() => {
    // If there is no state or no projectId, there's nothing to manage
    if (!location.state || !('projectId' in location.state)) return;

    // 2. Track the refresh count inside the router state (default to 0 if not present)
    const currentRefreshCount = location.state.refreshCount || 0;

    if (currentRefreshCount < 1) {
      // FIRST REFRESH: Increment the counter and update history state
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          refreshCount: currentRefreshCount + 1, // Changes 0 to 1
        },
      });
    } else {
      const remainingState = { ...location.state };
      delete remainingState.projectId;
      delete remainingState.refreshCount; // Clean up our counter key too

      // Replace the current history entry, preserving everything EXCEPT projectId
      navigate(location.pathname, {
        replace: true,
        state: remainingState,
      });
    }
  }, []); // Run only once when the page mounts

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const applyToolbarState = () => {
      const filterButton = container.querySelector('.button-group .table-utility-filter-btn')
        || container.querySelector('.button-group button[aria-label="Filter"]')
        || container.querySelector('.sr-toolbar-heading__filter-slot .table-utility-filter-btn')
        || container.querySelector('.sr-toolbar-heading__filter-slot button[aria-label="Filter"]');
      const filterButtonSlot = container.querySelector('.sr-toolbar-heading__filter-slot');

      if (filterButton && filterButtonSlot && filterButton.parentElement !== filterButtonSlot) {
        filterButtonSlot.appendChild(filterButton);
      }

      const toolbarButtons = container.querySelectorAll('.button-group button');
      toolbarButtons.forEach((buttonElement) => {
        const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
        const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn')
          || buttonElement.getAttribute('aria-label') === 'Filter'
          || Boolean(buttonElement.querySelector('.pi-filter'));

        buttonElement.classList.toggle('sr-filter-table-utility__reset-btn', isResetButton);
        buttonElement.classList.toggle('sr-filter-table-utility__filter-btn', isFilterButton);

        if (isFilterButton) {
          buttonElement.style.display = 'inline-flex';
          buttonElement.style.visibility = 'visible';
        }

        if (isResetButton) {
          buttonElement.style.display = 'none';
        }
      });
    };

    const adjustFilterOverlayPosition = () => {
      const filterButton = container.querySelector('.sr-toolbar-heading__filter-slot .table-utility-filter-btn')
        || container.querySelector('.sr-toolbar-heading__filter-slot button[aria-label="Filter"]')
        || container.querySelector('.button-group .table-utility-filter-btn');

      if (!filterButton) return;

      const buttonRect = filterButton.getBoundingClientRect();
      const minimumTop = Math.round(buttonRect.bottom + 8);
      const minimumLeft = Math.round(Math.max(8, buttonRect.right - 250));

      const overlaySelectors = [
        '.tu-filter-floating',
        '.tu-column-menu-root',
        '.tu-column-menu-child',
        '.table-column-tiered-menu.p-tieredmenu-overlay',
      ];

      document.querySelectorAll(overlaySelectors.join(', ')).forEach((overlayElement) => {
        const rect = overlayElement.getBoundingClientRect();
        const isNearToolbar = rect.right >= buttonRect.left - 32
          && rect.left <= buttonRect.right + 320;

        if (!isNearToolbar) return;

        const nextTop = Math.max(Math.round(rect.top), minimumTop);
        overlayElement.style.top = `${nextTop}px`;

        if (overlayElement.classList.contains('tu-column-menu-root')
          || overlayElement.classList.contains('table-column-tiered-menu')) {
          overlayElement.style.left = `${minimumLeft}px`;
        }
      });
    };
    const updatePendingProjectFilterSelection = () => {
      setPendingProjectFilterHasSelection(getProjectFilterState());
    };

    const timer = setTimeout(() => {
      applyToolbarState();
      updatePendingProjectFilterSelection();
    }, 400);
    const observer = new MutationObserver(() => {
      applyToolbarState();
      updatePendingProjectFilterSelection();
      requestAnimationFrame(adjustFilterOverlayPosition);
    });
    const documentObserver = new MutationObserver(updatePendingProjectFilterSelection);
    const handleDocumentClick = () => {
      setTimeout(updatePendingProjectFilterSelection, 0);
    };

    observer.observe(container, { childList: true, subtree: true });
    documentObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('input', updatePendingProjectFilterSelection, true);
    window.addEventListener('resize', adjustFilterOverlayPosition);
    window.addEventListener('scroll', adjustFilterOverlayPosition, true);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      documentObserver.disconnect();
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('input', updatePendingProjectFilterSelection, true);
      window.removeEventListener('resize', adjustFilterOverlayPosition);
      window.removeEventListener('scroll', adjustFilterOverlayPosition, true);
    };
  }, []);

  const selectedProjectIds = Array.isArray(filters?.projectIds) ? filters.projectIds : [];
  const scopedProjectId = selectedProjectIds.length === 1 ? selectedProjectIds[0] : null;
  const effectiveCanOpenNewRequest = pendingProjectFilterHasSelection === false
    ? false
    : canOpenNewRequest;

  const configuration = useMemo(() => ({
    generalSettings: {
      additionalPayloadData: {
        projectId: scopedProjectId,
      },
      enableApiFiltering: true,
      baseURL,
      userInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: { endpoint: '', enabled: false },
      },
      handler: {
        handleFilteredApiResponse: handleFilteredResponse,
      },
      headers: {
        token: userProfile?.access_token,
      },
      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,
      toolBarSettings: {
        showAddNew: false,
        showDownloadExcel: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: (
          <StaffingRequestToolbarHeading
            selectedCount={selectedCount}
            canShowEgm={canShowEgm}
            egmDetails={egmDetails}
            onOpenUpdateStatus={onOpenUpdateStatus}
            onCheckEgm={onCheckEgm}
            onOpenNewRequest={onOpenNewRequest}
            canOpenNewRequest={effectiveCanOpenNewRequest}
            filterButtonSlotRef={filterButtonSlotRef}
            hasAccess={hasAccess}
            hasApprovalAccess={hasApprovalAccess}
          />
        ),
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: 10,
      loaderSkeleton: {
        rows: 1,
        bodyCells: 1,
        columns: 1,
      },
      paginationConfig: {
        page: 1,
        limit: 10,
        total: 0,
        rowsPerPageOptions: [],
        showRowsPerPage: false,
      },
      showExpandCollapse: false,
      tableScrollHeight: '1px',
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  }), [
    baseURL,
    canOpenNewRequest,
    canShowEgm,
    egmDetails,
    onCheckEgm,
    handleFilteredResponse,
    onOpenNewRequest,
    onOpenUpdateStatus,
    selectedCount,
    userInfo,
    scopedProjectId,
    effectiveCanOpenNewRequest,
  ]);

  return (
    <div ref={containerRef} className="sr-filter-table-utility">
      {isUserInfoReady ? (
        <TableUtility
          key={tableResetToken}
          configuration={configuration}
          pageSize={10}
          showFilterOption
        />
      ) : null}
    </div>
  );
};

StaffingRequestFilterUtility.propTypes = {
  userProfile: PropTypes.shape({
    access_token: PropTypes.string,
    email: PropTypes.string,
    permissions: PropTypes.shape({}),
    roleDetails: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
    employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteConfiguration: PropTypes.shape({
      global_colors: PropTypes.shape({}),
    }),
  }),
  metaLoaded: PropTypes.bool,
  selectedCount: PropTypes.number.isRequired,
  canShowEgm: PropTypes.bool.isRequired,
  egmDetails: PropTypes.shape({
    currentEgm: PropTypes.number,
    projectedEgm: PropTypes.number,
    egmVariation: PropTypes.number,
    pendingFutureEffort: PropTypes.number,
    expectedFutureCost: PropTypes.number,
    estimatedCost: PropTypes.number,
    estimatedProfit: PropTypes.number,
    estimatedRevenue: PropTypes.number,
  }).isRequired,
  onOpenUpdateStatus: PropTypes.func.isRequired,
  onCheckEgm: PropTypes.func.isRequired,
  onOpenNewRequest: PropTypes.func.isRequired,
  canOpenNewRequest: PropTypes.bool.isRequired,
  hasApprovalAccess: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    projectIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }).isRequired,
  tableResetToken: PropTypes.number.isRequired,
  onFilteredResponse: PropTypes.func.isRequired,
};

StaffingRequestFilterUtility.defaultProps = {
  userProfile: null,
};

export default StaffingRequestFilterUtility;
